import classNames from "classnames";
import styles from "./FormsApplicationsList.module.scss";
import { useEffect, useState } from "react";
import { Button } from "@/components";
import { FormApplication } from "../../_types/formsApplications.types";
import { FormsApplicationListItem } from "../forms-application-list-item";
import { FormsApplicationDetailsModal } from "../forms-application-details-modal";
import { useMarkFormApplicationAsRead } from "../../_hooks/useMarkFormApplicationAsRead";

type FormsApplicationsListProps = {
  applications: Array<FormApplication>;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
};

export const FormsApplicationsList = ({
  applications,
  isLoading,
  isFetching,
  isError,
  onRetry,
}: FormsApplicationsListProps) => {
  const [selectedApplication, setSelectedApplication] =
    useState<FormApplication | null>(null);
  const markAsReadMutation = useMarkFormApplicationAsRead();

  useEffect(() => {
    if (!selectedApplication) return;

    const nextSelectedApplication =
      applications.find((application) => application.id === selectedApplication.id) ?? null;

    if (nextSelectedApplication) {
      setSelectedApplication(nextSelectedApplication);
    }
  }, [applications, selectedApplication]);

  const handleOpenDetails = (application: FormApplication) => {
    const nextApplication = application.read
      ? application
      : { ...application, read: true };

    setSelectedApplication(nextApplication);

    if (!application.read) {
      markAsReadMutation.mutate(application.id);
    }
  };

  if (isLoading) {
    return (
      <div className={styles["comp__state"]}>
        <div className={styles["comp__spinner"]} />
        <div>Loading form applications...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles["comp__state"]}>
        <div>Failed to load form applications.</div>
        <Button size="small" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  if (applications.length === 0) {
    return <div className={styles["comp__state"]}>No form applications found.</div>;
  }

  return (
    <>
      <div className={styles["comp"]} aria-busy={isFetching}>
        <div className={styles["comp__table-header"]}>
          <div
            className={classNames(
              styles["comp__column"],
              styles["comp__column--person"]
            )}
          >
            Sender
          </div>
          <div
            className={classNames(
              styles["comp__column"],
              styles["comp__column--category"]
            )}
          >
            Type
          </div>
          <div
            className={classNames(
              styles["comp__column"],
              styles["comp__column--content"]
            )}
          >
            Content
          </div>
          <div
            className={classNames(
              styles["comp__column"],
              styles["comp__column--date"]
            )}
          >
            Date
          </div>
        </div>

        <div className={styles["comp__list"]}>
          {applications.map((application) => (
            <FormsApplicationListItem
              key={application.id}
              application={application}
              onClick={() => handleOpenDetails(application)}
            />
          ))}
        </div>
      </div>

      <FormsApplicationDetailsModal
        application={selectedApplication}
        isOpen={selectedApplication !== null}
        isUpdatingReadStatus={markAsReadMutation.isPending}
        onClose={() => setSelectedApplication(null)}
      />
    </>
  );
};
